import { useMemo } from 'react';
import { Control, useFieldArray, UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useAuthUser } from 'auth/hooks';
import { TaskFormField } from 'bom/elements';
import { useLogActions } from 'bom/hooks';
import { ILog } from 'bom/types';
import { Form, FormField } from 'core/components';
import { InputType } from 'core/enums';
import { FormProvider, useFormContext, useNotificationContext } from 'core/states';
import { CorporateUserFormField } from 'corporate/elements';
import { Size } from 'theme/enums';

import { mapUpsertLogsForm } from './mappers';
import { upsertLogsSchema } from './schema';
import { TimeLogCalendar, TimeLogIntervalProvider } from './TimeLogCalendar';
import { IUpsertLogsFormData } from './types';

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  jobId: number;
  onSubmitSuccess?: (logs: ILog[]) => void;
}

export const UpsertLogs = ({ isOpen, onClose, jobId, onSubmitSuccess }: IProps) => {
  const { t } = useTranslation();

  const { clearNotification } = useNotificationContext();
  const { user } = useAuthUser();

  const { upsertLogs } = useLogActions();

  const defaultValues = useMemo<Partial<IUpsertLogsFormData>>(
    () => ({
      user: {
        id: user?.corporateUserId,
        name: `${user?.firstName} ${user?.lastName}`,
        userId: user?.id,
      },
      task: null,
      note: '',
      logs: [],
    }),
    [user?.corporateUserId, user?.firstName, user?.id, user?.lastName]
  );

  const onSubmit =
    (reset: UseFormReset<IUpsertLogsFormData>) => async (data: IUpsertLogsFormData) => {
      const { success, bomLogs } = await upsertLogs(mapUpsertLogsForm(jobId, data));

      if (!success) {
        return;
      }

      setTimeout(() => {
        handleOnClose(reset);
        onSubmitSuccess?.(bomLogs);
      }, 1000);
    };

  const handleOnClose = (reset?: UseFormReset<IUpsertLogsFormData>) => {
    reset?.(defaultValues);
    onClose();
    clearNotification();
  };

  return (
    <FormProvider schema={upsertLogsSchema()} defaultValues={defaultValues} resetDefaultValues>
      <Form
        isOpen={isOpen}
        onClose={handleOnClose}
        onSubmit={onSubmit}
        title={t('titleLogTime')}
        submitBtnLabel={t('buttonLabelAddLogs')}
        size={Size.LG}
        shouldSubmitOnKeyDown={false}
      >
        <CorporateUserFormField name="user" required />

        <TaskFormField name="task" required />

        <FormField
          name="note"
          type={InputType.TEXTAREA}
          inputProps={{
            label: t('labelNotes'),
            placeholder: t('placeholderNote'),
          }}
        />

        <TimeLogCalendarWithProvider label={t('labelTimeLogCalendar')} />
      </Form>
    </FormProvider>
  );
};

const TimeLogCalendarWithProvider = ({ label }: { label: string }) => {
  const { control } = useFormContext();

  const { append, update, remove } = useFieldArray<IUpsertLogsFormData, 'timeLogs', 'id'>({
    control: control as unknown as Control<IUpsertLogsFormData>,
    name: 'timeLogs',
  });

  return (
    <TimeLogIntervalProvider append={append} update={update} remove={remove}>
      <TimeLogCalendar label={label} />
    </TimeLogIntervalProvider>
  );
};
